import React, { useState } from 'react';
import { RSInput, RSButton } from 'reactsymbols-kit';
import Styles from '../../styles/colors';
import AddFSMController from '../../merode/controllers/AddFSMController';
import { useController, useAsynchronousAction, useFSMsForObjectType } from '../../hooks';
import FSMSelect from '../FSMSelect';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IModelVersion } from '../../stores/projects/model_version';
import type { IModel } from '../../stores/model';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSInputChange } from '../Inspector/rsForm';

/**
 * A typed controller method, re-typed to the loose action shape
 * `useAsynchronousAction` declares (see {@link AddStateForm} for the rationale).
 */
type AsyncActionLike = (...args: unknown[]) => Promise<unknown>;

/** The result an FSM-add action resolves to (matches `AddFSMController`). */
interface AddFSMResult {
  report: Report;
  fsm: string;
}

/**
 * Get the default name for the new FSM.
 */
function getDefaultName(
  modelVersion: IModelVersion,
  objectType: string,
  copyFrom: string | null | undefined,
  calculate: boolean
): string {
  let defaultName = '';
  // `content` is the loaded model here (non-null), matching the original JS.
  const metamodel = (modelVersion.content as unknown as IModel).metamodel;
  const original = copyFrom != null ? metamodel.getFSMById(copyFrom) : undefined;
  if (original != null) {
    if (original.type === 'DEFAULT') {
      // Copying the default FSM: "DEFAULTCopy" is a bad suggestion, since the
      // default FSM is always named "DEFAULT". Suggest `<ObjectType>LC`
      // instead (e.g. object type BUS -> "BUSLC"), and dedupe against the
      // object type's existing FSM names if that would already collide.
      const owner = original.objectType;
      const existingNames = owner.fsms.map((fsm) => fsm.name);
      const base = `${owner.name}LC`;
      defaultName = base;
      let suffix = 2;
      while (existingNames.includes(defaultName)) {
        defaultName = `${base}${suffix}`;
        suffix += 1;
      }
    } else {
      defaultName = `${original.name}Copy`;
    }
  }
  if (calculate) {
    const object = objectType != null ? metamodel.getObjectTypeById(objectType) : undefined;
    if (object != null) {
      defaultName = `${object.name}Calculated`;
    }
  }
  return defaultName;
}

/**
 * Get the title for the modal.
 */
function getTitle(copyFrom: string | null | undefined, calculate: boolean): string {
  let title = 'Add FSM';
  if (copyFrom != null) {
    title = 'Copy FSM';
  }
  if (calculate) {
    title = 'Calculate FSM';
  }
  return title;
}

interface FSMSelectorProps {
  /** The ModelVersion holding the available FSMs. */
  modelVersion: IModelVersion;
  /** The id of the object type whose FSMs to list. */
  objectType: string;
  /** The currently selected FSM ids. */
  fsms: string[];
  /** Setter for the selected FSM ids. */
  setFSMs: (fsms: string[]) => void;
}

/**
 * Wrapper component for FSMSelect that injects the available FSMs.
 */
const FSMSelector = ({ modelVersion, objectType, fsms, setFSMs }: FSMSelectorProps) => {
  // `content` is the loaded model here (non-null); the hook wants the `IModel`
  // interface and a `string` object-type id, both matching the original JS.
  const availableFSMs = useFSMsForObjectType(
    modelVersion.content as unknown as IModel,
    objectType
  );

  return <FSMSelect fsms={availableFSMs} selected={fsms} onChange={setFSMs} max={2} />;
};

interface AddFSMFormProps {
  /** The ModelVersion to add the FSM to. */
  modelVersion: IModelVersion;
  /** The id of the ObjectType to add the FSM to. */
  objectType: string;
  /** The id of the FSM to copy (when copying). */
  copyFrom?: string | null;
  /** True to add a calculated FSM, false otherwise. */
  calculate: boolean;
  /** Callback to cancel adding or copying an FSM. */
  onCancel?: () => void;
  /** Callback to send the report (and new FSM id) up. */
  onReport?: (report: Report, fsm: string) => void;
}

/**
 * Form to add, copy or calculate an FSM.
 *
 * Props:
 * - modelVersion: the ModelVersion to add the FSM to.
 * - objectType: the ObjectType to add the FSM to.
 * - copyFrom: the FSM to copy.
 * - calculate: true to add a calculated FSM, false otherwise.
 * - onCancel: callback to cancel adding or copying an FSM.
 * - onReport: callback to send the report up.
 */
const AddFSMForm = ({ modelVersion, objectType, copyFrom, calculate, onCancel, onReport }: AddFSMFormProps) => {
  // Store `IModelVersion` is the same loaded node the controller's narrower
  // `IModelVersion` describes; cast at the construction boundary only.
  const controller = useController(
    () =>
      new AddFSMController(modelVersion as unknown as IControllerModelVersion, objectType)
  );
  // The chosen action carries concrete arg/result types; the hook uses the loose
  // `(...args: unknown[]) => Promise<unknown>` shape, and forwards the resolved
  // `AddFSMResult` as `unknown`. Cast both boundaries — runtime unchanged.
  const addFSMAction = useAsynchronousAction(
    (calculate ? controller.calculateFSM : controller.addFSM) as AsyncActionLike,
    (result) => {
      const { report, fsm } = result as AddFSMResult;
      if (onReport != null) {
        onReport(report, fsm);
      }
    }
  );

  const [name, setName] = useState(getDefaultName(modelVersion, objectType, copyFrom, calculate));
  const [fsms, setFSMs] = useState<string[]>([]);

  const nameCheck = controller.checkNameInput(name);
  let valid = name != null && name.length > 0 && nameCheck.ok;
  if (calculate) {
    valid = valid && fsms != null && fsms.length === 2;
  }

  const busy = addFSMAction.busy;

  const onAddFSM = () => {
    if (calculate) {
      addFSMAction.execute(name, fsms[0], fsms[1]);
    } else {
      addFSMAction.execute(name, copyFrom);
    }
  };

  return (
    <div className="merlin-modal add-fsm-form">
      <div className="title">{getTitle(copyFrom, calculate)}</div>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) {
            onAddFSM();
          }
        }}
      >
        <div className="modal-body">
          <div className="container">
            <div className="row">
              <div className="col">
                <RSInput
                  autoFocus={true}
                  name="name"
                  label="FSM name"
                  value={name}
                  onChange={asRSChange<RSInputChange>((value: string) => setName(value))}
                  level={rsValue(nameCheck.ok ? null : 'danger')}
                  text={rsValue(nameCheck.ok ? null : nameCheck.message)}
                  disabled={busy}
                />
                {calculate && (
                  <FSMSelector
                    modelVersion={modelVersion}
                    objectType={objectType}
                    fsms={fsms}
                    setFSMs={setFSMs}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onCancel}
          />
          <RSButton
            type="submit"
            value={rsValue(busy ? null : copyFrom == null ? 'Add' : 'Copy')}
            background={Styles.blueGradient}
            disabled={!valid || busy}
            iconName={rsValue(busy ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(busy ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
};

export default AddFSMForm;
